/**
 * Composable: usePatientAppointmentRequest
 * Purpose: Manage patient-facing appointment scheduling state and actions
 * - Holds selected doctor/facility/date/time and patient form data
 * - Fetches available slots (unified slots API)
 * - Submits appointment requests (booking API)
 * - Fails gracefully (no blocking modals) when data is incomplete or backend errors occur
 */
import { useToast } from 'vue-toastification'

const usePatientAppointmentRequest = () => {

    const targetDoctorId = useState("targetDoctorId", () => "")
    const targetFacilityId = useState("targetFacilityId", () => "")
    /** Sets the active facility for scheduling */
    const setTargetFacilityId = (id: string) => {
        targetFacilityId.value = id || ""
    }
    /** Sets the active provider for scheduling */
    const setTargetDoctorId = (id: string) => {
        targetDoctorId.value = id || ""
    }
    const targetDateTime = useState("targetDateTime", () => "")
    const confirmationCode = useState("confirmationCode", () => "-")
    const ModalShow = useState("ModalShow", ()=> false)
    const setModalShow = (Input: boolean) =>{
        ModalShow.value = Input
    }
    
    const ModalState = useState("ModalState",()=> "default")
    const setModalState = (Input: string) => {
        ModalState.value = Input
    }

    const ModalMessage = useState("ModalMessage", () => "")
    const setModalMessage = (Input: string) => {
        ModalMessage.value = Input
    }


    const FormStatus = useState("FormStatus", () => "ok")
    const setFormStatus = (Input: string) => {
        FormStatus.value = Input
    }

    const FormStatusText = useState("FormStatusText", () => "")
    const setFormStatusText = (Input: string) => {
        FormStatusText.value = Input
    }

    const ptData = useState("ptData", () => ({
        pt_is_first_visit: false,
        pt_is_anonymous_visit: false,
        pt_dob: "",
        pt_name: "",
        pt_email: "",
        pt_phone: "",
        pt_visit_reason: "unselected",
        pt_language_preference: "unselected",
        pt_use_medical_insurance: true,
        pt_try_different_provider: true,
        pt_insurance_company_name: "",
        pt_insurance_member_id: "",
        pt_other_request: "",
        pt_email_sms_consent: false,
        pt_save_info: false,
        pt_term_accepted: false
    }))

    // Standardize: store date as YYYY-MM-DD for consistency across client/server
    const selectedApptDate = useState('selectedApptDate', ()=>"")
    const setSelectedApptDate = (input: string) => {
        // Accept either MM/DD/YYYY or YYYY-MM-DD; convert to canonical YYYY-MM-DD
        if (!input) { selectedApptDate.value = ""; return }
        if (/^\d{4}-\d{2}-\d{2}$/.test(input)) { selectedApptDate.value = input; return }
        const parts = input.split('/')
        if (parts.length === 3) {
            const [mm, dd, yyyy] = parts
            selectedApptDate.value = `${yyyy}-${(mm||'').padStart(2,'0')}-${(dd||'').padStart(2,'0')}`
            return
        }
        selectedApptDate.value = input
    }
    const AppointmentSlots = useState<any[]>('AppointmentSlots', ()=>[ ])
    const setAppointmentSlots = (Input: any[]) =>{
        AppointmentSlots.value = Input
    }

    const selectedAppointmentSlot = useState('selectedAppointmentSlot', ()=>"")
    const setSelectedAppointmentSlot = (Input: string) => {
        selectedAppointmentSlot.value = Input
    }

    // Slots load status for friendly UI messaging
    type SlotsStatus = 'idle' | 'loading' | 'ok' | 'error'
    const slotsStatus = useState<SlotsStatus>('slotsStatus', ()=>'idle')
    const setSlotsStatus = (s: SlotsStatus) => { slotsStatus.value = s }
    const slotsErrorMessage = useState('slotsErrorMessage', ()=>'')
    const setSlotsErrorMessage = (m: string) => { slotsErrorMessage.value = m || '' }

    /**
     * Fetches available slots for the current date and doctor/facility context.
     * Returns silently with empty slots when context is incomplete or an error occurs.
     */
    const getAppointmentSlots = async (_enableModal?: boolean) => {
        
        console.debug("Getting Appointment Slots for: ", selectedApptDate.value)
        setSlotsStatus('loading')
        setSlotsErrorMessage('')
        // Guard: require at least provider or facility (avoid backend 400)
        if (!targetDoctorId.value && !targetFacilityId.value) {
            console.debug("Skipping slot fetch: missing provider_id and facility_id")
            setAppointmentSlots([])
            setSlotsStatus('idle')
            return
        }
        try{
            // selectedApptDate is canonical YYYY-MM-DD now
            const isoDate = selectedApptDate.value
            const q = new URLSearchParams({ date: isoDate })
            if (targetDoctorId.value) q.set('provider_id', String(targetDoctorId.value))
            if (targetFacilityId.value) q.set('facility_id', String(targetFacilityId.value))
            const resp: any = await $fetch(`/api/appointments/slots?${q.toString()}`)
            console.debug ("Received Appt Data from Server:",resp)


            if (!Array.isArray(resp)) {
                console.error("Error Getting Appointment Data, Skipping...")
                setAppointmentSlots([])
                setSlotsStatus('error')
                setSlotsErrorMessage('当前在线预约服务暂时不可用，请稍后重试或联系诊所。')
                return
            }
            setAppointmentSlots(resp)
            setSlotsStatus('ok')
            
        }
        catch(e){
            console.error("Error Getting Appointment Data", e)
            // Fail gracefully without blocking the page
            setAppointmentSlots([])
            setSlotsStatus('error')
            setSlotsErrorMessage('当前在线预约服务暂时不可用，请稍后重试或联系诊所。')
        }
        
        
    }

    const ValidateRequest = () => {
        var result = {status: 'ok', messages: ""}
        if(ptData.value.pt_phone == ""){
            result.status = 'error'
            result.messages = result.messages + " " + "电话号码不能为空" + "\n"
        }
        if(ptData.value.pt_term_accepted == false){
            result.status = 'error'
            result.messages = result.messages + " " + "请阅读并同意条款" + "\n"
        }
        if(!ptData.value.pt_is_anonymous_visit){
            if (ptData.value.pt_name == ""){
                result.status = 'error'
                result.messages = result.messages + " " + "姓名不能为空" + "\n"
            }
            if (ptData.value.pt_dob == ""){
                result.status = 'error'
                result.messages = result.messages + " " + "出生日期不能为空" + "\n"
            }
        }
        if (ptData.value.pt_use_medical_insurance){
            if(ptData.value.pt_insurance_company_name == ""){
                result.status = 'error'
                result.messages = result.messages + " " + "请填写保险公司名称" + "\n"
            }
        }
        if (ptData.value.pt_visit_reason == "unselected"){
            result.status = 'error'
            result.messages = result.messages + " " + "请选择就诊原因" + "\n"
        }
        return result

    }
    
    /** Submits an appointment booking; validates form and selected slot */
    const submitAppointmentRequest = async () => {
        const toast = useToast()

        // toast.error("十分抱歉，系统维护中，当前无法提交预约，请稍后再试")

        if (selectedAppointmentSlot.value == ""){
            toast.error("请选择预约时间")
            return
        }
        const result = await ValidateRequest()
        if (result.status == 'error'){
            setModalState('error')
            setModalShow(true)
            toast.error("无法提交，请检查信息："+ "\n" + result.messages)
            return
        }
        const payload = {
            ...ptData.value,
            target_doctor_id: targetDoctorId.value,
            target_facility_id: targetFacilityId.value,
            selected_appt_date_time: selectedAppointmentSlot.value,
        }
        setModalState('appt-submit-wait')
        setModalShow(true)
        const resp = await $fetch('/api/appointments', {
            method: 'POST',
            body: JSON.stringify({
                payload: {
                    ...payload,
                    selected_appt_start: selectedAppointmentSlot.value,
                    selected_appt_date_time: selectedAppointmentSlot.value,
                }
            })
        })
        console.debug("Submit Appt: Received Response from Server:", resp)
        if (resp.status == 'ok')
        {   
            confirmationCode.value = resp.result.ID
            toast.success("提交成功，请等待诊所确认")
            setModalState('appt-submit-success')
            setModalShow(true)
            
        }
        else{
            toast.error("十分抱歉，系统维护中，当前无法提交预约，请稍后再试")
            setModalState('error')
            setModalShow(true)
        }
    }


    return {
        targetDoctorId, targetFacilityId, setTargetDoctorId, setTargetFacilityId, targetDateTime, ptData, submitAppointmentRequest, selectedApptDate, setSelectedApptDate, getAppointmentSlots, AppointmentSlots, setAppointmentSlots, selectedAppointmentSlot, setSelectedAppointmentSlot, ModalShow,
        setModalShow,
        ModalState,
        setModalState,
        ModalMessage,
        setModalMessage,
        confirmationCode,
        slotsStatus,
        slotsErrorMessage,
        setSlotsStatus,
        setSlotsErrorMessage,
    }

}

export default usePatientAppointmentRequest